import React from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';

import { getLayoutHierarchyAccessLevel, isLayoutViewActive } from '../../../store/selectors/layoutModeSelectors';
import ViewOptions from '../../dialogs/ViewOptions';
import { useAppSelector } from '../../hooks';
import AddItemButton from '../buttons/AddItemButton';
import { AddPlatesButton } from '../buttons/AddPlatesButton';
import AnalogModelVisibilityButton from '../buttons/AnalogModelVisibility';
import { AnalysisHistoryButton } from '../buttons/AnalysisHistoryButton';
import AnalyzeButton from '../buttons/AnalyzeButton';
import { DownloadButton } from '../buttons/DownloadButton';
import { DuplicateComponentButton } from '../buttons/DuplicateComponentButton';
import { EditTrussButton } from '../buttons/EditTrussButtton';
import FlipButton from '../buttons/FlipButton';
import FocusButton from '../buttons/FocusButton';
import GenerateLinkButton from '../buttons/GenerateLinkButton';
import { Layout3DCameraButton } from '../buttons/Layout3DCameraButton';
import { LayoutAutomaticDimensioningButtons } from '../buttons/LayoutAutomaticDimensioningButtons';
import { LayoutRenderModeButtons } from '../buttons/LayoutRenderModeButtons';
import { NotificationsButton } from '../buttons/NotificationsButton';
import { OptimizationButton } from '../buttons/OptimizationButton';
import ReferenceLinesVisibilityButton from '../buttons/ReferenceLinesVisibilityButton';
import { RenderModeToolbarMenuContextProvider } from '../buttons/RenderModeToolbarButton';
import ResetViewButton from '../buttons/ResetViewButton';
import ReverseButton from '../buttons/ReverseButton';
import { SnappingSettingsButton } from '../buttons/SnappingSettingsButton';
import { SnappingUnderlayButton } from '../buttons/SnappingUnderlayButton';
import SynchronizeButton from '../buttons/SynchronizeButton';
import { TrussLockOptionsButton } from '../buttons/TrussLockOptionsButton';
import { TrussSettingsButton } from '../buttons/TrussSettingsButton';
import { UndoRedoButtons } from '../buttons/UndoRedoButtons';
import UserDimensionsButton from '../buttons/UserDimensionsButton';
import { LayoutHierarchyToolbarLevelSelect } from './LayoutHierarchyToolbar';
import { ToolbarBase, ToolbarButtonRow } from './ToolbarBase';
import { ToolbarVerticalDivider } from './ToolbarVerticalDivider';

export const FULL_WIDTH_TOOLBAR_STYLING = {
  borderRadius: 'unset',
  zIndex: 2, // need to be above sidebars for shadow to be cast
} as const;

export function StandardToolbar() {
  const theme = useTheme();
  const condensed = useMediaQuery(theme.breakpoints.down('lg'));

  const inLayoutView = useAppSelector(isLayoutViewActive);
  const hierarchyAccess = useAppSelector(getLayoutHierarchyAccessLevel);

  // When editing this toolbar, please consider whether `MobileToolbar` should be updated as well.
  return (
    <ToolbarBase elevation={1} orientation="horizontal" sx={FULL_WIDTH_TOOLBAR_STYLING}>
      <ToolbarButtonRow
        orientation="horizontal"
        sx={{
          flex: '1 1 0px',
        }}
      >
        <Layout3DCameraButton visibleLabel />

        <ResetViewButton />
        <FocusButton />
        <GenerateLinkButton />
        <SynchronizeButton />
        <FlipButton />
        <ReverseButton />

        <ToolbarVerticalDivider />

        <LayoutAutomaticDimensioningButtons />
        <UserDimensionsButton />

        <ToolbarVerticalDivider />

        <RenderModeToolbarMenuContextProvider>
          <SnappingUnderlayButton />

          <AnalogModelVisibilityButton />
          <ReferenceLinesVisibilityButton />
          <LayoutRenderModeButtons />
        </RenderModeToolbarMenuContextProvider>
        <ViewOptions />
        <ToolbarVerticalDivider />
        <SnappingSettingsButton />
      </ToolbarButtonRow>

      {inLayoutView && hierarchyAccess.read && (
        <Box
          sx={{
            flex: '1 1 0px',

            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
          }}
        >
          <LayoutHierarchyToolbarLevelSelect />
        </Box>
      )}

      <ToolbarButtonRow
        orientation="horizontal"
        sx={{
          flex: '1 1 0px',

          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
        }}
      >
        <AddItemButton condensed={condensed} />
        <AnalyzeButton condensed={condensed} />
        <AddPlatesButton condensed={condensed} />
        <DownloadButton condensed={condensed} />
        <ToolbarVerticalDivider />
        <AnalysisHistoryButton />
        <OptimizationButton />
        <NotificationsButton />
        <TrussSettingsButton />
        <TrussLockOptionsButton />
        <DuplicateComponentButton />
        <EditTrussButton />
        <ToolbarVerticalDivider />
        <UndoRedoButtons />
      </ToolbarButtonRow>
    </ToolbarBase>
  );
}
